import React from 'react';
import { Button } from './button';
import { RefreshCw } from 'lucide-react';

/**
 * Universal Page Header Component
 * Provides consistent styling across all pages
 * 
 * @param {string} title - Main page title
 * @param {string} subtitle - Optional subtitle/description
 * @param {React.ReactNode} icon - Icon component to display
 * @param {Function} onRefresh - Optional refresh callback
 * @param {boolean} loading - Loading state for refresh button
 * @param {React.ReactNode} actions - Additional action buttons
 * @param {string} variant - Color variant: 'primary', 'purple', 'green', 'orange', 'red', 'blue'
 * @param {string} accentColor - Alternative to variant for accent color
 * @param {Array} stats - Optional stats array [{icon, label, value, color}]
 */
const PageHeader = ({ 
  title, 
  subtitle, 
  icon: Icon, 
  onRefresh, 
  loading = false,
  actions,
  variant = 'primary',
  accentColor,
  stats
}) => {
  // Use accentColor as variant if provided
  const effectiveVariant = accentColor || variant;

  // Accent colors for icons and highlights
  const accents = {
    primary: 'text-blue-400',
    purple: 'text-purple-400',
    green: 'text-emerald-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    yellow: 'text-yellow-400',
    cyan: 'text-cyan-400',
  };

  const statColors = {
    blue: 'bg-blue-500/20 text-blue-300',
    purple: 'bg-purple-500/20 text-purple-300',
    green: 'bg-emerald-500/20 text-emerald-300',
    orange: 'bg-orange-500/20 text-orange-300',
    red: 'bg-red-500/20 text-red-300',
    yellow: 'bg-yellow-500/20 text-yellow-300',
    cyan: 'bg-cyan-500/20 text-cyan-300',
    primary: 'bg-blue-500/20 text-blue-300',
  };

  const accent = accents[effectiveVariant] || accents.primary;

  return (
    <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-5 text-white shadow-lg border border-slate-600/50">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className={`p-2.5 bg-white/10 rounded-lg ${accent}`}>
              <Icon className="w-6 h-6" />
            </div>
          )}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-slate-300 mt-1 text-sm">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {actions}
          {onRefresh && (
            <Button 
              onClick={onRefresh}
              disabled={loading}
              size="sm"
              className="bg-white/10 hover:bg-white/20 text-white border-0"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      {stats && stats.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stats.map((stat, idx) => {
              const StatIcon = stat.icon;
              const colorClass = statColors[stat.color] || statColors.primary;
              return (
                <div key={idx} className={`flex items-center gap-3 p-2 rounded-lg ${colorClass}`}>
                  {StatIcon && <StatIcon className="w-4 h-4" />}
                  <div>
                    <p className="text-lg font-bold">{stat.value}</p>
                    <p className="text-xs opacity-80">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeader;
